import { CardTab, TabbedCard } from '@backstage/core-components';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Link,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import NewReleasesIcon from '@mui/icons-material/NewReleases';

import { useAsyncRetry } from 'react-use';
import { FeedbackCard } from '../FeedbackCard';
import { LoadingCard } from '../LoadingCard';

interface RfcItem {
  title: string;
  url: string;
  practiceType: string;
  new: boolean;
}

const linkInfo = {
  title: 'Go to RFC Confluence site',
  link: 'https://principal.atlassian.net/wiki/spaces/DC/database/990414205',
};

type RfcBackendData = {
  url: string;
  title: string;
  closed: boolean;
  updatedAt: string;
  practiceType: string;
  rfcStatus: string;
  category: {
    name: string;
  };
  label: { name: string; color: string };
};

const sortRfcData = (data: RfcBackendData[] | undefined) => {
  const readyForReview: RfcItem[] = [];
  const inDevelopment: RfcItem[] = [];
  const backlog: RfcItem[] = [];
  const approved: RfcItem[] = [];

  if (data) {
    data.forEach((item: any) => {
      const now = new Date();
      const updatedAt = new Date(item.updatedAt);
      const diff = Math.abs(now.getTime() - updatedAt.getTime());
      const diffDays = Math.ceil(diff / (1000 * 3600 * 24));
      if (item.category.name === 'RFC Backlog') {
        backlog.push({
          title: item.title,
          url: item.url,
          practiceType: item.practiceType,
          new: diffDays < 7,
        });
      } else if (
        item.category.name === 'Engineering Standards / Development Process'
      ) {
        if (item.label.name === 'ready-for-review') {
          readyForReview.push({
            title: item.title,
            url: item.url,
            practiceType: item.practiceType,
            new: diffDays < 7,
          });
        } else if (item.label.name === 'in-development') {
          inDevelopment.push({
            title: item.title,
            url: item.url,
            practiceType: item.practiceType,
            new: diffDays < 7,
          });
        } else if (item.label.name === 'approved') {
          approved.push({
            title: item.title,
            url: item.url,
            practiceType: item.practiceType,
            new: diffDays < 7,
          });
        }
      }
    });
  }

  return { readyForReview, inDevelopment, backlog, approved };
};

interface RfcListItemProps {
  index: number;
  title: string;
  url: string;
  practiceType: string;
  isNew: boolean;
}

const RfcListItem = (props: RfcListItemProps) => {
  return (
    <ListItem key={props.index}>
      <ListItemText>
        <Link href={props.url}>{props.title}</Link> ({props.practiceType})
        {props.isNew && (
          <Tooltip title="Recently updated">
            <NewReleasesIcon style={{ height: 16 }} />
          </Tooltip>
        )}
      </ListItemText>
    </ListItem>
  );
};

const useStyles = makeStyles({
  fullHeightCard: {
    height: 'auto !important',
  },
});

const RfcListData = (props: { sortedRfcData: RfcItem[] }) => {
  if (props.sortedRfcData.length < 1) {
    return <div>Nothing here, yet.</div>;
  }
  return (
    <List>
      {props.sortedRfcData.map((item: RfcItem, i) => (
        <RfcListItem
          index={i}
          title={item.title}
          url={item.url}
          practiceType={item.practiceType}
          isNew={item.new}
        />
      ))}
    </List>
  );
};

export const RfcCard = () => {
  const config = useApi(configApiRef);
  const identity = useApi(identityApiRef);
  const styles = useStyles();

  const backendHost = config.getString('backend.baseUrl');
  const { value, loading } = useAsyncRetry(async (): Promise<
    RfcBackendData[]
  > => {
    const { token } = await identity.getCredentials();
    const response = await fetch(`${backendHost}/api/rfcs/rfcs`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.status === 200) {
      return (await response.json()) as RfcBackendData[];
    }
    return [];
  }, []);

  if (loading) {
    return <LoadingCard />;
  }

  if (value !== undefined && value?.length > 0) {
    const sortedRfcData = sortRfcData(value);

    return (
      <div className={styles.fullHeightCard}>
        {/* TODO: TabbedCard doesn't provide a way to add a class to the card, but we need full height */}
        <TabbedCard title="Request for Comments (RFCs)" deepLink={linkInfo}>
          <CardTab label="Ready for review">
            <RfcListData sortedRfcData={sortedRfcData.readyForReview} />
          </CardTab>
          <CardTab label="In development">
            <RfcListData sortedRfcData={sortedRfcData.inDevelopment} />
          </CardTab>
          <CardTab label="Approved">
            <RfcListData sortedRfcData={sortedRfcData.approved} />
          </CardTab>
        </TabbedCard>
      </div>
    );
  }

  return <FeedbackCard />;
};
